import { collection, doc, getDoc, getDocs, limit, query, where, Firestore, DocumentSnapshot } from 'firebase/firestore';

/** Auth authority is an exact document ID or one explicit uid binding. Email,
 * name and matching person numbers never authorize account/profile selection.
 * Conflicting direct/linked records require review, even with equal person IDs.
 */
export async function resolveAuthUserDocument(database: Firestore, uid: string): Promise<DocumentSnapshot | null> {
  if (typeof uid !== 'string' || !uid || uid.trim() !== uid || /[\/\x00-\x1f]/.test(uid))
    throw new Error('Invalid authenticated account ID.');
  const policy = process.env.REACT_APP_PERSON_IDENTITY_POLICY;
  if (policy !== undefined && policy !== 'legacy' && policy !== 'strict-v1')
    throw new Error('Invalid person identity policy.');
  if (policy === 'strict-v1') {
    // Activate only with matching owner-get/server-write-only binding rules.
    // Immutable review history remains backend-only; clients trust the current
    // protected binding and verify the exact target rather than guessing aliases.
    const binding = await getDoc(doc(database, '_personAccountBindings', uid));
    if (binding.exists()) {
      const value = binding.data();
      if (value.schemaVersion !== 1 || value.status !== 'active' || value.authUid !== uid
        || typeof value.userId !== 'string' || !value.userId || value.userId.trim() !== value.userId
        || /[\/\x00-\x1f]/.test(value.userId) || !Number.isSafeInteger(value.personId) || value.personId <= 0
        || !Number.isSafeInteger(value.revision) || value.revision < 1
        || typeof value.operationDigest !== 'string' || !/^[a-f0-9]{64}$/.test(value.operationDigest))
        throw new Error('Invalid reviewed account binding. Identity review is required.');
      const target = await getDoc(doc(database, 'users', value.userId));
      const linked = await getDocs(query(collection(database, 'users'), where('uid', '==', uid), limit(2)));
      const data = target.exists() ? target.data() : undefined;
      if (!data || data.uid !== uid || data.personId !== value.personId
        || data.accountBindingOperation !== value.operationDigest
        || data.mergedInto || data.mergedIntoUserId
        || (data.accountBindingState !== undefined && data.accountBindingState !== 'active')
        || linked.size !== 1 || linked.docs[0].id !== target.id)
        throw new Error('Stale reviewed account binding. Identity review is required.');
      return target;
    }
  }
  const direct = await getDoc(doc(database, 'users', uid));
  const linked = await getDocs(query(collection(database, 'users'), where('uid', '==', uid), limit(2)));
  if (linked.size > 1) throw new Error('Multiple profiles are linked to this account. Identity review is required.');
  const candidates = new Map<string, DocumentSnapshot>();
  if (direct.exists()) candidates.set(direct.id, direct);
  linked.docs.forEach(snapshot => candidates.set(snapshot.id, snapshot));
  if (candidates.size > 1) throw new Error('Conflicting account/profile links require identity review.');
  const selected = Array.from(candidates.values())[0];
  if (!selected) return null;
  const data = selected.data() || {};
  if ((data.uid != null && data.uid !== '' && data.uid !== uid)
    || data.mergedInto || data.mergedIntoUserId
    || (data.accountBindingState !== undefined && data.accountBindingState !== 'active'))
    throw new Error('This profile has a changed account binding. Identity review is required.');
  if (data.personId != null && (!Number.isSafeInteger(data.personId) || data.personId <= 0))
    throw new Error('This profile has an invalid runner identity. Identity review is required.');
  return selected;
}
